import {Injectable} from "@angular/core";
import {INotice, INoticeMetadata, IMetadataSpec} from "./types";
import {BehaviorSubject, Subject, Subscription, forkJoin} from "rxjs";
import {NoticeService, PaygDropdown, Taxpayer, User} from "../../../swaggerServices";
import {parseMetadataValue} from "./util";
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {asMap, asMapV} from "../../shared/util/asMap";

const getMetadataValueForFormControl = (v?: any) => {
  if (v == null) {
    return "";
  } else if (typeof v === "boolean") {
    return v;
  } else if (typeof v === "string") {
    return v;
  } else {
    return String(v);
  }
};

const getDefaultValue = (spec: IMetadataSpec) => {
  if (spec.dataType === "boolean") {
    return false;
  } else {
    return "";
  }
};

const linkNotificationOverrideKey = "RubixLINKSendConfirmationEmail";

@Injectable()
export class NoticesStateService {
  selectedNotice?: INotice;

  // metadata
  metadataSub?: Subscription;
  noticeMetadata?: INoticeMetadata[];
  metadataSpecs?: any[]; // = globalMetadataSpecs; // should be IMetadataSpec but typescript doesn't seem to infer our string correctly
  sortedMetadata?: {md: INoticeMetadata, spec: IMetadataSpec}[];
  origValues?: {[key: string]: string|boolean|null};
  formGroup?: UntypedFormGroup;
  formGroupSubs: Subscription[] = [];
  modified?: {[key: string]: boolean};
  numModified: number = 0;
  savingNotice?: INotice;
  savingMetadata?: INotice;
  userMap: Map<number, User> = new Map();
  searchRequested: Subject<void> = new Subject<void>();
  allowEditAll: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private noticeService: NoticeService) { }

  selectNotice(notice?: INotice) {
    this.selectedNotice = notice;
    if (notice) {
      this.refreshMetadata(notice, true);
    }
  }

  refreshMetadata(notice: INotice, clearExisting: boolean = false) {
    if (notice.id === this.selectedNotice.id) {
      if (this.metadataSub) {
        this.metadataSub.unsubscribe();
      }
      if (clearExisting) {
        this.noticeMetadata = null;
      }
      this.metadataSub = forkJoin([
        this.noticeService.getMetadataForNotice(notice.id),
        this.noticeService.getMetadataSpecForNotice(notice.id),
      ]).subscribe(([metadata, specs]) => {
        const dropdownsByName = asMapV(specs.dropdowns, (dd: PaygDropdown) => dd.name, (dd: PaygDropdown) => dd.values);
        this.metadataSpecs = specs.specs.map(s => ({
          key: s.metadataSpec.key,
          displayName: s.metadataSpec.displayName || s.metadataSpec.key,
          dataType: s.metadataSpec.dataType,
          format: s.metadataSpec.format,
          editable: s.metadataSpec.editable,
          hidden: s.hidden,
          order: s.order,
          dropdowns: s.metadataSpec.dataType === "dropdown" ? dropdownsByName.get(s.metadataSpec.format) : null,
        })).sort((a, b) => (a.order || Number.MAX_VALUE) - (b.order || Number.MAX_VALUE));
        const metadataSpecsByKey = asMap(this.metadataSpecs, s => s.key);

        this.noticeMetadata = (metadata || []).map(m => ({
          ...m,
          structuredValue: parseMetadataValue(m, metadataSpecsByKey.get(m.key), specs.docshareBaseUrl),
          // TODO(mike): expose step metadata inputs?
          // title: m.format ? `Format: ${m.format}` : null,
        }));
        this.setupMetadata(this.noticeMetadata);
      }, e => {
        this.noticeMetadata = null;
        this.setupMetadata(this.noticeMetadata);
      });
    }
  }

  setupMetadata(metadata: INoticeMetadata[]) {
    if (this.formGroupSubs) {
      this.formGroupSubs.forEach(s => s.unsubscribe());
    }
    this.formGroupSubs = [];

    if (!metadata) {
      this.sortedMetadata = null;
      this.origValues = null;
      this.formGroup = null;
      this.modified = null;
      this.numModified = 0;
      return;
    }
    const metadataByKey = asMap(metadata, md => md.key);
    // const knownKeys = new Set(this.metadataSpecs.map(spec => spec.key));

    const mds = this.metadataSpecs.map(spec => ({
      spec,
      md: metadataByKey.get(spec.key) || { key: spec.key, value: null, structuredValue: { isArray: false, value: getDefaultValue(spec) } },
    }));

    this.sortedMetadata = mds.filter(({md, spec}) => !md.structuredValue.isArray);
    this.origValues = {};
    this.modified = {};
    this.numModified = 0;
    const disabledControls = [];
    const formGroup = new UntypedFormGroup({});
    for (const {md, spec} of this.sortedMetadata) {
      if (!Array.isArray(md.value)) {
        const v = getMetadataValueForFormControl(md.structuredValue.value);
        this.origValues[spec.key] = v;

        const validators = [];
        if (spec.dropdowns) {
          validators.push(formControl => {
            const isValid = !formControl.value || spec.dropdowns.includes(formControl.value);
            return isValid ? null : { dropdown: { valid: false } };
          });
        }

        const control = new UntypedFormControl(v, { validators });
        if (!spec.editable) {
          if (!this.allowEditAll.getValue()) {
            control.disable();
          }
          disabledControls.push(control);
        }
        this.modified[md.key] = false;
        this.formGroupSubs.push(control.valueChanges.subscribe(newValue => {
          this.modified[md.key] = newValue !== this.origValues[md.key];
          this.numModified = Object.values(this.modified).filter(x => x).length;
        }));
        formGroup.addControl(md.key, control);
      }
    }
    this.formGroup = formGroup;
    this.formGroupSubs.push(this.allowEditAll.subscribe(allow => {
      for (const ctrl of disabledControls) {
        if (allow) {
          ctrl.enable();
        } else {
          ctrl.disable();
        }
      }
    }));
  }

  cleanUp() {
    if (this.metadataSub) {
      this.metadataSub.unsubscribe();
    }
    this.metadataSub = null;
    this.selectedNotice = null;
    this.noticeMetadata = null;
  }

  linkNotificationEnabled(): boolean | null {
    if (!this.noticeMetadata) {
      // still loading
      return null;
    }
    if (this.selectedNotice && this.formGroup) {
      const control = this.formGroup.controls[linkNotificationOverrideKey];
      return control ? control.value : null;
    }
    return null;
  }

  setLinkNotificationEnabled({ taxpayer, enabled }: { taxpayer: Taxpayer, enabled: boolean }) {
    if (this.selectedNotice && this.selectedNotice.atlasId === taxpayer.atlasId) {
      const control = this.formGroup.controls[linkNotificationOverrideKey];
      if (control) {
        control.setValue(!!enabled);
        this._saveMetadata([{key: linkNotificationOverrideKey, value: !!enabled}], false);
      }
    }
  }

  markAsComplete(notice: INotice) {
    // -1 is the completed step
    // TODO(mike): Don't hard-code -1, use some other signifier from the list of steps,
    // e.g. the name END? or perhaps just let them choose the step.
    this.savingNotice = notice;
    this.noticeService.moveNoticeToStep(notice.id, -1).subscribe(resp => {
      this.savingNotice = null;
      this.triggerSearch();
    }, e => {
      this.savingNotice = null;
    });
  }

  saveMetadata() {
    const updates = [...Object.entries(this.modified)]
      .filter(([key, modified]) => modified)
      .map(([key, modified]) => ({
        key,
        value: this.formGroup.controls[key].value,
      }));
    this._saveMetadata(updates);
  }

  _saveMetadata(updates, fullRefresh: boolean = true) {
    const n = this.selectedNotice;
    this.savingMetadata = n;
    this.noticeService.setMetadataForNotice(n.id, { metadata: updates }).subscribe(resp => {
      this.savingMetadata = null;
      if (n.id === this.selectedNotice.id) {
        if (fullRefresh) {
          this.refreshMetadata(n);
        } else {
          for (const {key, value} of updates) {
            this.origValues[key] = value;
            const control = this.formGroup.controls[key];
            if (control) {
              control.setValue(value);
            }
          }
        }
      }
    }, e => {
      this.savingMetadata = null;
    });
  }

  resetValue(spec: IMetadataSpec) {
    this.formGroup.controls[spec.key].setValue(this.origValues[spec.key]);
  }

  resolveUser(userId?: number) {
    return this.userMap.get(userId);
  }

  setUsers(users?: User[]) {
    this.userMap = users ? asMap(users, u => u.id) : new Map();
  }

  triggerSearch() {
    this.searchRequested.next();
  }

  setAllowEditing(val: boolean) {
    this.allowEditAll.next(val);
  }

  getAllowEditing(): boolean {
    return this.allowEditAll.getValue();
  }
}
